import * as React from 'react'
import { IconButton, Modal, Portal, Text } from 'react-native-paper'
import { useTranslation } from 'react-i18next'
import { Image, View, StyleSheet } from 'react-native'
import { AppNavigation } from '../../../navigation/types'
import { getReturnPath } from '../../../navigation/build-return-path'
import { Row } from '../../../@ui/containers/grid/row'
import { GooglePNG, SmartPhone, WechatPNG } from '../../../utils/meldd-icons'
import { AuthContext } from '../../../contexts/auth-context'
import { goAfterLogin } from './utils'
import { useLastLoginMethod } from './use-last-login-method.hook'
import { LastUsedTag } from './last-used.tag'

type LoginRequiredModal = {
  //
}

export type LoginRequiredRef = {
  open: (navigation: AppNavigation) => void
  close: () => void
}

/**
 * A little modal that appears when we login with SSO and the account doesn't exists.
 * Will allow us to register a new account based in this SSO, or login with our exisisting account
 * and linked to the SSO.
 */
export const LoginRequiredModal = React.forwardRef<
  LoginRequiredRef,
  LoginRequiredModal
>(({}, ref) => {
  const { t } = useTranslation('login_register_reset')
  const { isAnonymous } = React.useContext(AuthContext)
  const [visible, setVisible] = React.useState(false)
  const [navigation, setNavigation] = React.useState<AppNavigation | null>(null)
  const { lastLoginMethod } = useLastLoginMethod()
  const returnPath = navigation
    ? getReturnPath(navigation) ?? 'ListNodes'
    : 'ListNodes'

  React.useEffect(() => {
    if (isAnonymous || !visible || !navigation) {
      return
    }
    setVisible(false)
    goAfterLogin({ navigation, returnPath })
  }, [isAnonymous, navigation, returnPath, visible])

  const hideModal = () => setVisible(false)
  React.useImperativeHandle(ref, () => ({
    // The Modal is used in the auth outside the navigation stack, we can not just use `useNavigation`
    open: (navigation: AppNavigation) => {
      setNavigation(navigation)
      setVisible(true)
    },
    close: () => setVisible(false),
  }))

  return (
    <>
      <Portal>
        <Modal
          visible={visible}
          onDismiss={hideModal}
          contentContainerStyle={styles.container}
        >
          <Text style={styles.title}>{t('login-required')}</Text>
          <Text style={styles.description}>{t('login-single-click')}</Text>
          <View style={styles.actions}>
            <Row justifyContent="center" alignItems="center" wrap={false}>
              <View style={styles.loginMethod}>
                <IconButton
                  mode="outlined"
                  icon={() => (
                    <Image
                      source={GooglePNG}
                      style={[{ width: 16, height: 16 }]}
                    />
                  )}
                  onPress={() => {
                    setVisible(false)
                    navigation?.navigate('LoginMobileNumber', {
                      returnPath,
                      loginMethod: 'google',
                    })
                  }}
                />
                <LastUsedTag
                  show={lastLoginMethod === 'google'}
                  text={t('last-used-login-method')}
                  style={styles.lastUsedTag}
                />
              </View>
              <View style={styles.loginMethod}>
                <IconButton
                  mode="outlined"
                  icon={() => (
                    <Image
                      source={WechatPNG}
                      style={[{ width: 16, height: 16 }]}
                    />
                  )}
                  onPress={() => {
                    setVisible(false)
                    navigation?.navigate('LoginMobileNumber', {
                      returnPath,
                      loginMethod: 'wechat',
                    })
                  }}
                />
                <LastUsedTag
                  show={lastLoginMethod === 'wechat'}
                  text={t('last-used-login-method')}
                  style={styles.lastUsedTag}
                />
              </View>
              <View style={styles.loginMethod}>
                <IconButton
                  mode="outlined"
                  icon={SmartPhone}
                  onPress={() => {
                    setVisible(false)
                    navigation?.navigate('LoginMobileNumber', {
                      returnPath,
                      loginMethod: 'otp',
                    })
                  }}
                />
                <LastUsedTag
                  show={lastLoginMethod === 'otp'}
                  text={t('last-used-login-method')}
                  style={styles.lastUsedTag}
                />
              </View>
            </Row>
          </View>
          <Row
            justifyContent="center"
            alignItems="center"
            wrap={false}
            style={{ marginTop: 4 }}
          >
            <Text style={styles.cancelText} onPress={() => setVisible(false)}>
              {t('cancel')}
            </Text>
          </Row>
        </Modal>
      </Portal>
    </>
  )
})

const styles = StyleSheet.create({
  container: {
    backgroundColor: 'white',
    padding: 20,
    margin: 20,
    borderRadius: 8,
  },
  title: { fontSize: 20, fontWeight: 'bold', marginBottom: 10 },
  description: { marginBottom: 16 },
  actions: { marginBottom: 18 },
  loginMethod: { alignItems: 'center', minWidth: 72, position: 'relative' },
  lastUsedTag: {
    position: 'absolute',
    top: 52,
    fontSize: 11,
    opacity: 0.75,
  },
  cancelText: { opacity: 0.7 },
})
